import { useState, type FormEvent } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AudienceKind, CampaignChannel } from '@tabita/contracts';
import { formatDateTime } from '@tabita/i18n';
import { useI18n } from '@/i18n/i18n-provider';
import { communicationsApi } from '@/services/communications.api';
import { peopleApi } from '@/services/people.api';
import { groupsApi } from '@/services/groups.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, Spinner, StatusBadge } from '@/components/ui';

function errorMessage(error: unknown, t: ReturnType<typeof useI18n>['t']): string {
  return error instanceof ApiError
    ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
    : t('errors.INTERNAL_ERROR');
}

/**
 * Compose a new draft, or open an existing one.
 *
 * A DRAFT is a live form: every field is editable, and the audience preview
 * refetches as soon as the channel or audience selection changes. Anything
 * else (SENDING/SENT/FAILED) is a completed fact — this renders it read-only
 * rather than a form nobody can submit anyway.
 */
export function CampaignFormPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const campaignQuery = useQuery({
    queryKey: ['communications', 'campaigns', id],
    queryFn: () => communicationsApi.get(id!),
    enabled: Boolean(id),
  });

  const tagsQuery = useQuery({
    queryKey: ['people', 'tags'],
    queryFn: ({ signal }) => peopleApi.listTags(signal),
  });

  const groupsQuery = useQuery({
    queryKey: ['groups', 'picker'],
    queryFn: ({ signal }) => groupsApi.list({ limit: 100 }, signal),
  });

  // A "Send message" action on a group's page arrives here as
  // `/communications/new?groupId=...` — pre-selects that group rather than
  // starting the operator on a blank ALL/TAGS choice.
  const prefilledGroupId = !id ? searchParams.get('groupId') : null;

  const [channel, setChannel] = useState<CampaignChannel | null>(null);
  const [subject, setSubject] = useState<string | null>(null);
  const [body, setBody] = useState<string | null>(null);
  const [audienceKind, setAudienceKind] = useState<AudienceKind | null>(
    prefilledGroupId ? 'GROUP' : null,
  );
  const [audienceTagIds, setAudienceTagIds] = useState<string[] | null>(null);
  const [audienceGroupId, setAudienceGroupId] = useState<string | null>(prefilledGroupId);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [confirmingSend, setConfirmingSend] = useState(false);

  const existing = campaignQuery.data;
  const effectiveChannel = channel ?? existing?.channel ?? 'EMAIL';
  const effectiveSubject = subject ?? existing?.subject ?? '';
  const effectiveBody = body ?? existing?.body ?? '';
  const effectiveAudienceKind = audienceKind ?? existing?.audienceKind ?? 'ALL';
  const effectiveTagIds = audienceTagIds ?? existing?.audienceTagIds ?? [];
  const effectiveGroupId = audienceGroupId ?? existing?.audienceGroupId ?? null;

  const isDraft = !existing || existing.status === 'DRAFT';

  const previewQuery = useQuery({
    queryKey: [
      'communications',
      'audience-preview',
      effectiveChannel,
      effectiveAudienceKind,
      effectiveTagIds,
      effectiveGroupId,
    ],
    queryFn: () =>
      communicationsApi.previewAudience({
        channel: effectiveChannel,
        audienceKind: effectiveAudienceKind,
        audienceTagIds: [...effectiveTagIds],
        audiencePersonIds: [],
        audienceGroupId: effectiveGroupId,
      }),
    enabled:
      isDraft &&
      (effectiveAudienceKind === 'ALL' ||
        (effectiveAudienceKind === 'TAGS' && effectiveTagIds.length > 0) ||
        (effectiveAudienceKind === 'GROUP' && effectiveGroupId !== null)),
  });

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        channel: effectiveChannel,
        subject: effectiveChannel === 'EMAIL' ? effectiveSubject : null,
        body: effectiveBody,
        audienceKind: effectiveAudienceKind,
        audienceTagIds: [...effectiveTagIds],
        audiencePersonIds: [],
        audienceGroupId: effectiveGroupId,
      };
      return id ? communicationsApi.update(id, payload) : communicationsApi.create(payload);
    },
    onSuccess: async (response) => {
      setSaveError(null);
      await queryClient.invalidateQueries({ queryKey: ['communications', 'campaigns'] });
      if (!id) void navigate(`/communications/${response.id}`, { replace: true });
    },
    onError: (error: unknown) => setSaveError(errorMessage(error, t)),
  });

  const send = useMutation({
    mutationFn: () => communicationsApi.send(id!),
    onSuccess: async () => {
      setSendError(null);
      setConfirmingSend(false);
      await queryClient.invalidateQueries({ queryKey: ['communications', 'campaigns'] });
    },
    onError: (error: unknown) => {
      setSendError(errorMessage(error, t));
      setConfirmingSend(false);
    },
  });

  const archive = useMutation({
    mutationFn: () => communicationsApi.archive(id!),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['communications', 'campaigns'] });
      void navigate('/communications', { replace: true });
    },
    onError: (error: unknown) => setSaveError(errorMessage(error, t)),
  });

  if (id && campaignQuery.isPending) {
    return <Spinner label={t('common.states.loading')} />;
  }

  if (id && campaignQuery.isError) {
    return <Alert tone="danger">{errorMessage(campaignQuery.error, t)}</Alert>;
  }

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    save.mutate();
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>
            {existing
              ? (existing.subject ?? t(`communications.campaign.channelLabel.${existing.channel}`))
              : t('communications.campaign.new')}
          </h1>
          {existing && (
            <p className="page-count">
              <StatusBadge
                status={
                  existing.status === 'SENT'
                    ? 'ACTIVE'
                    : existing.status === 'FAILED'
                      ? 'INACTIVE'
                      : existing.status
                }
                label={t(`communications.campaign.status.${existing.status}`)}
              />
            </p>
          )}
        </div>
      </header>

      {!isDraft && existing && (
        <div className="settings-status">
          <div>
            <p className="settings-status-title">{t('communications.campaign.resultsTitle')}</p>
            <p className="settings-status-hint">
              {t('communications.campaign.reachCount', {
                values: { sent: existing.sentCount, total: existing.recipientCount },
              })}
            </p>
            <p className="settings-status-hint">
              {t('communications.campaign.failedSkipped', {
                values: { failed: existing.failedCount, skipped: existing.skippedCount },
              })}
            </p>
            {existing.sentAt && (
              <p className="settings-status-hint">
                {t('communications.campaign.sentAt', {
                  values: { date: formatDateTime(existing.sentAt, locale) },
                })}
              </p>
            )}
          </div>
        </div>
      )}

      <form className="settings-card" onSubmit={handleSubmit}>
        <div className="form-grid">
          <div className="field">
            <label className="field-label" htmlFor="campaign-channel">
              {t('communications.campaign.channel')}
            </label>
            <select
              id="campaign-channel"
              className="field-input"
              value={effectiveChannel}
              disabled={!isDraft}
              onChange={(event) => setChannel(event.target.value as CampaignChannel)}
            >
              <option value="EMAIL">{t('communications.campaign.channelLabel.EMAIL')}</option>
              <option value="SMS">{t('communications.campaign.channelLabel.SMS')}</option>
              <option value="WHATSAPP">{t('communications.campaign.channelLabel.WHATSAPP')}</option>
            </select>
          </div>

          {effectiveChannel === 'EMAIL' && (
            <div className="field">
              <label className="field-label" htmlFor="campaign-subject">
                {t('communications.campaign.subject')}
              </label>
              <input
                id="campaign-subject"
                type="text"
                className="field-input"
                value={effectiveSubject}
                disabled={!isDraft}
                onChange={(event) => setSubject(event.target.value)}
                required
              />
            </div>
          )}
        </div>

        {effectiveChannel === 'WHATSAPP' && (
          <Alert tone="warning">{t('communications.campaign.whatsappTemplateHint')}</Alert>
        )}

        <div className="field">
          <label className="field-label" htmlFor="campaign-body">
            {t('communications.campaign.body')}
          </label>
          <textarea
            id="campaign-body"
            className="field-input oauth-key-textarea"
            value={effectiveBody}
            disabled={!isDraft}
            onChange={(event) => setBody(event.target.value)}
            maxLength={4000}
            required
          />
        </div>

        <div className="form-grid">
          <div className="field">
            <label className="field-label" htmlFor="campaign-audience">
              {t('communications.campaign.audience')}
            </label>
            <select
              id="campaign-audience"
              className="field-input"
              value={effectiveAudienceKind}
              disabled={!isDraft}
              onChange={(event) => setAudienceKind(event.target.value as AudienceKind)}
            >
              <option value="ALL">{t('communications.campaign.audienceAll')}</option>
              <option value="TAGS">{t('communications.campaign.audienceTags')}</option>
              <option value="GROUP">{t('communications.campaign.audienceGroup')}</option>
            </select>
          </div>

          {previewQuery.data && (
            <div className="field">
              <span className="field-label">{t('communications.campaign.audiencePreview')}</span>
              <p className="settings-password-stored">
                {t('communications.campaign.audiencePreviewCount', {
                  values: {
                    reachable: previewQuery.data.reachableCount,
                    matched: previewQuery.data.matchedCount,
                  },
                })}
              </p>
            </div>
          )}
        </div>

        {effectiveAudienceKind === 'TAGS' && (
          <div className="field">
            <span className="field-label">{t('communications.campaign.pickTags')}</span>
            {tagsQuery.data?.length === 0 && (
              <p className="settings-status-hint">{t('communications.campaign.noTags')}</p>
            )}
            <div className="oauth-provider-list">
              {tagsQuery.data?.map((tag) => (
                <label key={tag.id} className="checkbox">
                  <input
                    type="checkbox"
                    disabled={!isDraft}
                    checked={effectiveTagIds.includes(tag.id)}
                    onChange={(event) => {
                      const next = event.target.checked
                        ? [...effectiveTagIds, tag.id]
                        : effectiveTagIds.filter((tagId) => tagId !== tag.id);
                      setAudienceTagIds(next);
                    }}
                  />
                  <span>{tag.name}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {effectiveAudienceKind === 'GROUP' && (
          <div className="field">
            <label className="field-label" htmlFor="campaign-group">
              {t('communications.campaign.pickGroup')}
            </label>
            <select
              id="campaign-group"
              className="field-input"
              disabled={!isDraft}
              value={effectiveGroupId ?? ''}
              onChange={(event) => setAudienceGroupId(event.target.value || null)}
            >
              <option value="">{t('communications.campaign.pickGroupPlaceholder')}</option>
              {groupsQuery.data?.data.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {saveError && <Alert tone="danger">{saveError}</Alert>}

        {isDraft && (
          <div className="oauth-provider-form-actions">
            <Button type="submit" variant="primary" loading={save.isPending}>
              {t('communications.campaign.saveDraft')}
            </Button>
            {existing && (
              <Button
                type="button"
                variant="ghost"
                loading={archive.isPending}
                onClick={() => archive.mutate()}
              >
                {t('communications.campaign.discardDraft')}
              </Button>
            )}
          </div>
        )}
      </form>

      {isDraft && existing && (
        <div className="settings-card settings-card-test">
          <h2 className="settings-card-title">{t('communications.campaign.sendTitle')}</h2>
          <p className="settings-status-hint">{t('communications.campaign.sendWarning')}</p>

          {sendError && <Alert tone="danger">{sendError}</Alert>}

          {!confirmingSend ? (
            <Button
              type="button"
              variant="danger"
              onClick={() => setConfirmingSend(true)}
              disabled={previewQuery.data?.reachableCount === 0}
            >
              {t('communications.campaign.sendNow')}
            </Button>
          ) : (
            <div className="inline-confirm">
              <p>{t('communications.campaign.sendConfirm')}</p>
              <Button type="button" variant="ghost" onClick={() => setConfirmingSend(false)}>
                {t('common.actions.cancel')}
              </Button>
              <Button
                type="button"
                variant="danger"
                loading={send.isPending}
                onClick={() => send.mutate()}
              >
                {t('common.actions.confirm')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
